import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { notificationCounts } from '../messages/NotificationStatusChip';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import { fetchJson, getApiBase } from '../../apiBase';

const FONT_LINK_ID = 'rst-font-link';

const api = () => {
  const base = `${getApiBase()}/admin`;
  return {
    flat: (school) => `${base}/reading-speed-tests/${encodeURIComponent(school)}/results`,
    classes: (school) => `${base}/classes/${encodeURIComponent(school)}`,
    studentsByClass: (school, className) => `${base}/students/${encodeURIComponent(school)}/by-class/${encodeURIComponent(className)}`,
  };
};

const ensureFont = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600&display=swap';
  document.head.appendChild(link);
};

const accentFont = '"Space Grotesk", "Poppins", sans-serif';

const deriveStudentId = (student) => {
  const candidate =
    student?.student_id ??
    student?.id ??
    student?.studentId ??
    student?.student_no ??
    student?.studentNo ??
    student?.uuid ??
    student?.phone;
  if (candidate === undefined || candidate === null || candidate === '') {
    return '';
  }
  return String(candidate);
};

const formatStudentDisplayName = (student) => {
  const joined = [student?.name, student?.surname].filter(Boolean).join(' ').trim();
  const rawName = student?.student_name || student?.full_name || student?.display_name || joined;
  const id = deriveStudentId(student);
  return (rawName || (id ? `Öğrenci ${id}` : 'Öğrenci')).trim();
};

const extractScoreNumber = (value) => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).replace(',', '.');
  const match = normalized.match(/-?\d+(?:\.\d+)?/);
  return match ? Number.parseFloat(match[0]) : null;
};

const formatAverageDisplay = (values = []) => {
  const numericValues = values.filter((val) => typeof val === 'number' && !Number.isNaN(val));
  if (!numericValues.length) return '-';
  const total = numericValues.reduce((sum, val) => sum + val, 0);
  const avg = total / numericValues.length;
  return Number.isInteger(avg) ? String(avg) : avg.toFixed(1);
};

const labelExam = (title, book) => {
  const base = title || 'İsimsiz Test';
  if (!book) return base;
  return `${base} • ${book}`;
};

export default function ReadingSpeedTestTable({ schoolId, onError }) {
  const apis = useMemo(() => api(), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', className: null, examTitle: null });
  const [classes, setClasses] = useState([]);
  const [examTitles, setExamTitles] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    ensureFont();
  }, []);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apis.classes(schoolId));
      if (res.ok) {
        const data = await res.json();
        const classes = Array.isArray(data)
          ? data
              .map((c) => {
                if (typeof c === 'string') return { id: null, name: c };
                const name = c?.code || c?.name || c;
                return { id: c.id, name };
              })
              .filter((c) => c.name)
          : [];
        classes.sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
        setClasses(classes);
      }
    } catch (err) {
      console.warn('classes load failed', err);
    }
  }, [apis, schoolId]);

  const loadRows = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await fetchJson(apis.flat(schoolId));
      if (!res.ok) throw new Error('flat_failed');
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRows(items);
      const uniqueTitles = Array.from(new Set(items.map((item) => labelExam(item.test_title, null))));
      uniqueTitles.sort((a, b) => String(a).localeCompare(String(b), 'tr-TR'));
      setExamTitles(uniqueTitles);
    } catch (err) {
      console.error(err);
      onError && onError('Sonuç listesi getirilemedi');
    } finally {
      setLoading(false);
    }
  }, [apis, schoolId, onError]);

  useEffect(() => {
    loadRows();
    loadClasses();
  }, [loadRows, loadClasses]);

  useEffect(() => {
    if (!filters.className || !schoolId || studentsByClass[filters.className]) {
      return;
    }
    let cancelled = false;
    const loadStudents = async () => {
      setLoadingStudents(true);
      try {
        const classObj = classes.find((c) => c.name === filters.className);
        if (!classObj || !classObj.id) return;
        const res = await fetchJson(apis.studentsByClass(schoolId, classObj.id));
        if (!res.ok) throw new Error('students_failed');
        const data = await res.json();
        if (!cancelled) {
          setStudentsByClass((prev) => ({ ...prev, [filters.className]: Array.isArray(data) ? data : [] }));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          onError && onError('Öğrenciler yüklenemedi');
        }
      } finally {
        if (!cancelled) {
          setLoadingStudents(false);
        }
      }
    };
    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [filters.className, apis, schoolId, studentsByClass, classes, onError]);

  const pivotData = useMemo(() => {
    if (!filters.className) {
      return { exams: [], students: [] };
    }

    const baseRows = rows.filter((row) => row.class_name === filters.className);
    const roster = studentsByClass[filters.className] || [];
    const examSet = new Set();
    const examMeta = new Map();
    const studentMap = new Map();

    const ensureStudentEntry = (studentId, displayName) => {
      if (!studentId) return;
      if (!studentMap.has(studentId)) {
        studentMap.set(studentId, {
          student_id: studentId,
          student_name: displayName,
          entries: {},
        });
      } else if (!studentMap.get(studentId).student_name && displayName) {
        studentMap.get(studentId).student_name = displayName;
      }
    };

    roster.forEach((student) => {
      const studentId = deriveStudentId(student);
      const displayName = formatStudentDisplayName(student);
      ensureStudentEntry(studentId, displayName);
    });

    baseRows.forEach((row) => {
      const titleLabel = labelExam(row.test_title, null);
      if (filters.examTitle && titleLabel !== filters.examTitle) return;
      examSet.add(titleLabel);
      examMeta.set(titleLabel, { rawTitle: row.test_title, book: null });

      const studentId = String(row.student_id || '');
      ensureStudentEntry(studentId, row.student_name);

      if (studentId) {
        studentMap.get(studentId).entries[titleLabel] = row.result_value ?? '-';
      }
    });

    let students = Array.from(studentMap.values()).map((student) => {
      const numericScores = Object.values(student.entries).map(extractScoreNumber);
      return {
        ...student,
        average: formatAverageDisplay(numericScores),
      };
    });

    if (filters.search) {
      const needle = filters.search.toLowerCase();
      students = students.filter((student) => {
        if ((student.student_name || '').toLowerCase().includes(needle)) {
          return true;
        }
        if ((student.average || '').toLowerCase().includes(needle)) {
          return true;
        }
        return Object.entries(student.entries).some(([examTitle, value]) => {
          return examTitle.toLowerCase().includes(needle) || String(value || '').toLowerCase().includes(needle);
        });
      });
    }

    students.sort((a, b) => String(a.student_name || '').localeCompare(String(b.student_name || ''), 'tr-TR'));
    const exams = Array.from(examSet).sort((a, b) => String(a || '').localeCompare(String(b || ''), 'tr-TR'));

    return { exams, students, examMeta };
  }, [rows, filters, studentsByClass]);

  const stats = useMemo(() => notificationCounts(rows), [rows]);

  const gradientBg = 'linear-gradient(135deg, rgba(14,165,233,0.15), rgba(16,185,129,0.12))';

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid rgba(15,118,110,0.2)', background: gradientBg }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2}>
          <div>
            <Typography variant="h6" sx={{ fontFamily: accentFont, fontWeight: 600 }}>
              Okuma Hızı Testleri · Özet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: accentFont }}>
              Şubelere göre sonuçları filtreleyin, gönderilmeyenleri takip edin.
            </Typography>
          </div>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Filtreleri sıfırla">
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilters({ search: '', className: null, examTitle: null })}
              >
                Sıfırla
              </Button>
            </Tooltip>
            <Tooltip title="Yenile">
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                  disabled={loading}
                  onClick={loadRows}
                >
                  Yenile
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} mt={3}>
          <Paper sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: 'rgba(14,165,233,0.15)' }} elevation={0}>
            <Typography variant="body2" sx={{ fontFamily: accentFont }}>Toplam kayıt</Typography>
            <Typography variant="h4" sx={{ fontFamily: accentFont, fontWeight: 600 }}>{stats.total}</Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: 'rgba(251,191,36,0.2)' }} elevation={0}>
            <Typography variant="body2" sx={{ fontFamily: accentFont }}>Kuyrukta</Typography>
            <Typography variant="h4" sx={{ fontFamily: accentFont, fontWeight: 600 }}>{stats.pending}</Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: 'rgba(52,211,153,0.2)' }} elevation={0}>
            <Typography variant="body2" sx={{ fontFamily: accentFont }}>Gönderildi</Typography>
            <Typography variant="h4" sx={{ fontFamily: accentFont, fontWeight: 600 }}>{stats.sent}</Typography>
          </Paper>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip label={`Paylaşım bekliyor: ${stats.unshared}`} size="small" variant="outlined" />
        <Chip label={`Başarısız: ${stats.failed}`} size="small" color="error" variant="outlined" />
        <Chip label={`Durum belirsiz: ${stats.unknown}`} size="small" color="warning" variant="outlined" />
        <Chip label={`Atlandı: ${stats.skipped}`} size="small" variant="outlined" />
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Öğrenci veya test ara"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            options={classes}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?.name === value}
            value={classes.find((c) => c.name === filters.className) || null}
            onChange={(_, value) => setFilters((prev) => ({ ...prev, className: value?.name || null }))}
            renderInput={(params) => <TextField {...params} size="small" label="Şube" />}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            options={examTitles}
            value={filters.examTitle}
            onChange={(_, value) => setFilters((prev) => ({ ...prev, examTitle: value }))}
            renderInput={(params) => <TextField {...params} size="small" label="Test" />}
            sx={{ minWidth: 220 }}
          />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {!filters.className ? (
          <Alert severity="info">Lütfen sonuçları görmek için bir şube seçin.</Alert>
        ) : loading || loadingStudents ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={36} />
          </Box>
        ) : !pivotData.exams.length ? (
          <Alert severity="info">Bu şubeye ait okuma hızı sonucu bulunamadı.</Alert>
        ) : !pivotData.students.length ? (
          <Alert severity="info">Seçilen filtrelere uygun öğrenci bulunamadı.</Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={240}>Öğrenci</TableCell>
                  {pivotData.exams.map((exam) => (
                    <TableCell key={exam} align="center">{exam}</TableCell>
                  ))}
                  <TableCell align="center">Ortalama</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pivotData.students.map((student) => (
                  <TableRow key={student.student_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.student_name}</Typography>
                      <Typography variant="caption" color="text.secondary">#{student.student_id}</Typography>
                    </TableCell>
                    {pivotData.exams.map((exam) => (
                      <TableCell key={`${student.student_id}-${exam}`} align="center">
                        <Typography variant="body2" sx={{ fontFamily: accentFont, fontWeight: 500 }}>
                          {student.entries[exam] ?? '-'}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontFamily: accentFont, fontWeight: 600 }}>
                        {student.average || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
